import React, { useState, useMemo, useEffect } from 'react'
import {
    Box,
    Typography,
    CircularProgress,
    TextField,
    MenuItem,
} from '@material-ui/core'
import { Pagination } from '@material-ui/lab'

import { useAuditResults } from '../../hooks/useAuditResults'
import { useLeaseAuditDetail } from '../../hooks/useLeaseAuditDetail'
import { AuditResultsDialog } from './AuditResultsDialog'
import { AuditAttributeDetail } from './AuditAttributeDetail'
import { AuditTable } from './AuditTable'
import {
    AttributeSummary,
    AuditAttribute,
    AuditAttributeValue,
    AuditValueStatus,
} from '../../types'
import { statusFromValuesMatch } from './auditValueStatus'
import { useAuditRootStyles } from './styles'
import { HEADER_HEIGHT, COLLAPSABLE_SELECTOR_HEIGHT } from '../constants'

interface AuditTabProps {
    auditId: number
    onGenerateReport: () => void
    isGeneratingReport: boolean
    isCollapsableSelectorOpen: boolean
}

export const AuditRoot: React.FC<AuditTabProps> = ({
    auditId,
    isCollapsableSelectorOpen,
}) => {
    const headerHeight = HEADER_HEIGHT
    const collapsableSelectorHeight = isCollapsableSelectorOpen
        ? COLLAPSABLE_SELECTOR_HEIGHT
        : 0
    const classes = useAuditRootStyles({
        headerHeight,
        collapsableSelectorHeight,
    })

    // Pagination state
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(50)
    const mismatchPage = 1
    const mismatchPageSize = 1

    const [
        attributeDialogAttribute,
        setAttributeDialogAttribute,
    ] = useState<AuditAttribute | null>(null)

    // Get lease audit data for global term status filter
    const { leaseAudit, updateLeaseAudit } = useLeaseAuditDetail({ auditId })

    const termStatusFilter = leaseAudit?.term_status_filter || 'CURRENT_ONLY'

    // Fetch audit results with filtering
    const {
        attributes,
        attributeSummary,
        residentTerms,
        totalCount,
        totalPages,
        rentRollFile,
        isLoading,
        error,
        updateDocumentValue,
        isUpdatingDocumentValue,
        saveReviewNote,
        isSavingReviewNote,
        mismatchResidentTerms,
        mismatchTotalCount,
        mismatchTotalPages,
        isLoadingMismatches,
        mismatchError,
    } = useAuditResults({
        auditId,
        termStatusFilter,
        page,
        pageSize,
        mismatchAttributeId: attributeDialogAttribute?.id ?? null,
        mismatchEnabled: Boolean(attributeDialogAttribute),
    })

    useEffect(() => {
        if (totalPages > 0 && page > totalPages) {
            setPage(totalPages)
        }
    }, [page, totalPages])

    const attributeSummaryById = useMemo(() => {
        if (!attributeSummary) {
            return {}
        }

        return attributeSummary.reduce((acc, summary) => {
            const summaryId = summary.attribute_id ?? summary.id
            if (typeof summaryId === 'number') {
                acc[summaryId] = summary
            }
            return acc
        }, {} as Record<number, AttributeSummary>)
    }, [attributeSummary])

    const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
        setPage(value)
    }

    const handleHeaderClick = (attribute: AuditAttribute) => {
        setAttributeDialogAttribute(attribute)
    }

    // Dialog state
    const [dialogState, setDialogState] = useState<{
        open: boolean
        rentRollCoordinates: string | null
        leaseDocumentFile: string
        documentPageNumber: number | null
        attributeName: string
        rentRollValue: string | null
        documentValue: string | null
        conflictValue: string | null
        status: AuditValueStatus
        reviewNote: string | null
        reviewNoteAuthor: string | null
        termId: number
        attributeId: number
    } | null>(null)

    // Handle cell click
    const handleCellClick = (
        attributeValue: AuditAttributeValue,
        attributeName: string,
        leaseDocumentFile: string,
        termId: number,
    ) => {
        const status = statusFromValuesMatch(
            attributeValue.rent_roll_value,
            attributeValue.document_value,
            attributeValue.values_match,
        )

        setDialogState({
            open: true,
            rentRollCoordinates: attributeValue.rent_roll_coordinates,
            leaseDocumentFile,
            documentPageNumber: attributeValue.document_page_number,
            attributeName,
            rentRollValue: attributeValue.rent_roll_value,
            documentValue: attributeValue.document_value,
            conflictValue: attributeValue.conflict_value ?? null,
            status: status,
            reviewNote: attributeValue.review_note ?? null,
            reviewNoteAuthor: attributeValue.review_note_author_name ?? null,
            termId,
            attributeId: attributeValue.attribute_id,
        })
    }

    const handleUpdateDocumentValue = async (nextValue: string | null) => {
        if (!dialogState) return

        await updateDocumentValue({
            residentTermId: dialogState.termId,
            attributeId: dialogState.attributeId,
            documentValue: nextValue,
        })

        setDialogState((prevState) =>
            prevState
                ? {
                      ...prevState,
                      documentValue: nextValue,
                      conflictValue: null,
                  }
                : prevState,
        )
    }

    const handleUpdateMismatchDocumentValue = async (
        residentTermId: number,
        attributeId: number,
        documentValue: string | null,
    ) => {
        await updateDocumentValue({
            residentTermId,
            attributeId,
            documentValue,
        })
    }

    const handleSaveDialogReviewNote = async (note: string | null) => {
        if (!dialogState) return

        const response = await saveReviewNote({
            residentTermId: dialogState.termId,
            attributeId: dialogState.attributeId,
            note,
        })
        setDialogState((prevState) =>
            prevState
                ? {
                      ...prevState,
                      reviewNote: response.data.review_note,
                      reviewNoteAuthor: response.data.review_note_author_name,
                  }
                : prevState,
        )
    }

    const handleSaveMismatchReviewNote = async (
        residentTermId: number,
        attributeId: number,
        note: string | null,
    ) => {
        await saveReviewNote({
            residentTermId,
            attributeId,
            note,
        })
    }

    // Prepare cell classes for AuditCell component
    const cellClasses = {
        dataCell: classes.dataCell,
        matchedCell: classes.matchedCell,
        notMatchedCell: classes.notMatchedCell,
        missingRentRollCell: classes.missingRentRollCell,
        missingDocumentCell: classes.missingDocumentCell,
        cellContent: classes.cellContent,
        cellValue: classes.cellValue,
        cellIndicators: classes.cellIndicators,
    }

    if (isLoading) {
        return (
            <Box className={classes.loadingState}>
                <CircularProgress />
            </Box>
        )
    }

    if (error) {
        return (
            <Box className={classes.emptyState}>
                <Typography variant="h6" gutterBottom>
                    Error loading audit results
                </Typography>
                <Typography variant="body2">
                    {error.message || 'An unexpected error occurred'}
                </Typography>
            </Box>
        )
    }

    if (totalCount === 0) {
        return (
            <Box className={classes.emptyState}>
                <Typography variant="h6" gutterBottom>
                    No audit results available
                </Typography>
                <Typography variant="body2">
                    Complete the previous steps to generate audit results
                </Typography>
            </Box>
        )
    }
    return (
        <Box className={classes.container}>
            {/* Table */}
            <AuditTable
                attributes={attributes}
                attributeSummaryById={attributeSummaryById}
                residentTerms={residentTerms}
                cellClasses={cellClasses}
                tableContainerClassName={classes.tableContainer}
                headerCellClassName={classes.headerCell}
                headerCellClickableClassName={classes.headerCellClickable}
                headerCellContentClassName={classes.headerCellContent}
                mismatchBadgeClassName={classes.mismatchBadge}
                onHeaderClick={handleHeaderClick}
                onCellClick={handleCellClick}
            />

            {/* Pagination */}
            {totalPages > 1 && (
                <Box className={classes.pagination}>
                    <TextField
                        select
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value))
                            setPage(1)
                        }}
                        variant="outlined"
                        size="small"
                        style={{ width: 80 }}
                    >
                        {[25, 50, 100].map((size) => (
                            <MenuItem key={size} value={size}>
                                {size}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                    />
                    <Typography variant="body2" color="textSecondary">
                        {totalCount > 0 ? (page - 1) * pageSize + 1 : 0} -{' '}
                        {Math.min(
                            (page - 1) * pageSize +
                                (residentTerms?.length ?? 0),
                            totalCount,
                        )}{' '}
                        of {totalCount}
                    </Typography>
                </Box>
            )}

            {/* Dialog */}
            {dialogState &&
                (rentRollFile || leaseAudit?.audit_type === 'INTEGRATED') && (
                    <AuditResultsDialog
                        open={dialogState.open}
                        onClose={() => setDialogState(null)}
                        rentRollFile={rentRollFile}
                        rentRollCoordinates={dialogState.rentRollCoordinates}
                        leaseDocumentFile={dialogState.leaseDocumentFile}
                        documentPageNumber={dialogState.documentPageNumber}
                        attributeName={dialogState.attributeName}
                        rentRollValue={dialogState.rentRollValue}
                        documentValue={dialogState.documentValue}
                        conflictValue={dialogState.conflictValue}
                        status={dialogState.status}
                        reviewNote={dialogState.reviewNote}
                        reviewNoteAuthor={dialogState.reviewNoteAuthor}
                        onSaveReviewNote={handleSaveDialogReviewNote}
                        isSavingReviewNote={isSavingReviewNote}
                        onUpdateDocumentValue={handleUpdateDocumentValue}
                        isUpdatingDocumentValue={isUpdatingDocumentValue}
                    />
                )}

            {attributeDialogAttribute && (
                <AuditAttributeDetail
                    auditId={auditId}
                    open={Boolean(attributeDialogAttribute)}
                    onClose={() => setAttributeDialogAttribute(null)}
                    attribute={attributeDialogAttribute}
                    mismatchResidentTerms={mismatchResidentTerms}
                    rentRollFile={rentRollFile}
                    isLoading={isLoadingMismatches}
                    error={mismatchError}
                    page={mismatchPage}
                    totalPages={mismatchTotalPages}
                    totalCount={mismatchTotalCount}
                    onUpdateDocumentValue={handleUpdateMismatchDocumentValue}
                    isUpdatingDocumentValue={isUpdatingDocumentValue}
                    onSaveReviewNote={handleSaveMismatchReviewNote}
                    isSavingReviewNote={isSavingReviewNote}
                />
            )}
        </Box>
    )
}
